import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { IconChartBar } from "@tabler/icons-react";
import { TransactionDateRangePicker } from "../transactions/TransactionDateRangePicker";

const toolbarLabelClass = "text-sm text-[#52667f]";
const toolbarSelectClass =
  "h-9 min-w-[9rem] appearance-none rounded-md border border-[#d9e1ea] bg-[#f4f7fb] pl-3 pr-8 text-sm font-medium leading-9 text-[#2d3f57] outline-none ring-blue-500 focus:ring-2";
const toolbarButtonClass =
  "inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-[#d5dde7] bg-[#f8fafc] px-3 text-sm font-medium text-[#2d3f57] hover:bg-slate-100 disabled:opacity-60";

function ToolbarSelect({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label?: string;
  value: number | "";
  onChange: (value: number | "") => void;
  children: ReactNode;
}) {
  return (
    <>
      {label && (
        <label htmlFor={id} className={toolbarLabelClass}>
          {label}:
        </label>
      )}
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) =>
            onChange(e.target.value ? Number(e.target.value) : "")
          }
          className={toolbarSelectClass}
        >
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7f99]"
          aria-hidden
        />
      </div>
    </>
  );
}

type CollectorOption = { id: number; username: string };
type GroupOption = { id: number; name: string };

export function ReceivablesHeaderToolbar({
  showCollectorFilter,
  showGroupFilter,
  filterCollectorId,
  onFilterCollectorIdChange,
  collectors,
  filterGroupId,
  onFilterGroupIdChange,
  groups,
  startDate,
  endDate,
  onDateRangeChange,
  showCharts,
  onToggleCharts,
}: {
  showCollectorFilter: boolean;
  showGroupFilter: boolean;
  filterCollectorId: number | "";
  onFilterCollectorIdChange: (value: number | "") => void;
  collectors: CollectorOption[];
  filterGroupId: number | "";
  onFilterGroupIdChange: (value: number | "") => void;
  groups: GroupOption[];
  startDate?: string;
  endDate?: string;
  onDateRangeChange: (from?: string, to?: string) => void;
  showCharts?: boolean;
  onToggleCharts?: () => void;
}) {
  return (
    <div
      className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end"
      role="toolbar"
      aria-label="Receivables filters and actions"
    >
      {showCollectorFilter ? (
        <ToolbarSelect
          id="receivables-collector-filter"
          value={filterCollectorId}
          onChange={onFilterCollectorIdChange}
        >
          <option value="">All collectors</option>
          {collectors.map((collector) => (
            <option key={collector.id} value={collector.id}>
              {collector.username}
            </option>
          ))}
        </ToolbarSelect>
      ) : null}
      {showGroupFilter ? (
        <ToolbarSelect
          id="receivables-group-filter"
          value={filterGroupId}
          onChange={onFilterGroupIdChange}
        >
          <option value="">All groups</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </ToolbarSelect>
      ) : null}

      <span className={toolbarLabelClass}>Dates:</span>
      <TransactionDateRangePicker
        dateFrom={startDate}
        dateTo={endDate}
        commitDateRange={onDateRangeChange}
        rangeHint="Show receivables between"
        ariaRangeKind="Receivables date range"
      />

      {onToggleCharts ? (
        <button
          type="button"
          onClick={onToggleCharts}
          aria-pressed={showCharts}
          className={toolbarButtonClass}
        >
          <IconChartBar size={16} aria-hidden />
          {showCharts ? "Hide charts" : "Show charts"}
        </button>
      ) : null}
    </div>
  );
}
